import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { GuardianInstallation } from '../../api/types';
import type { ListState } from '../common/AsyncList';

/**
 * Lifecycle of one product in one customer tenant, as Guardian will record
 * it (docs/ARCHITECTURE-END-TO-END.md, "Installation states").
 */
export type InstallationState =
  | 'ReadyToDeploy'
  | 'Installing'
  | 'Installed'
  | 'AwaitingCustomerAzure'
  | 'Live'
  | 'InUse'
  | 'Failed'
  | 'Suspended'
  | 'Removed';

/** One installation record from FirstIQ Guardian. */
export interface Installation {
  tenantId: string;
  customerName: string;
  productId: string;
  productName: string;
  version: string;
  siteUrl: string;
  state: InstallationState;
  installedBy?: string;
  updatedAt: string;
}

/** Installation records for every customer tenant, from FirstIQ Guardian via GET /api/installations. */
export function useInstallations(): ListState<Installation> {
  const q = useQuery({ queryKey: ['installations'], queryFn: () => api<GuardianInstallation[]>('installations') });
  return {
    loading: q.isLoading,
    error: q.error ? (q.error as Error).message : null,
    items: (q.data ?? []).map((i) => ({
      tenantId: i.tenantId,
      customerName: i.CustomerName || i.tenantId,
      productId: i.appId,
      productName: i.appId,
      version: i.Version || '',
      siteUrl: i.SiteUrl || '',
      state: i.State as InstallationState,
      installedBy: i.UpdatedBy || undefined,
      updatedAt: i.StateChangedAt,
    })),
  };
}
